/**
 * Plan Builder Cart Enhancer V2 - Clean CSS Injection Approach
 * Much simpler and more reliable than inline styles
 */

class PlanBuilderCartEnhancer {
  constructor() {
    this.cssInjected = false;
    this.init();
  }

  init() {
    // Inject CSS once
    this.injectCSS();
    
    // Run enhancement when page loads
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.enhanceCart());
    } else {
      this.enhanceCart();
    }

    // Re-run when cart updates
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.enhanceCart(), 300);
    });
    
    document.addEventListener('cart:refresh', () => {
      setTimeout(() => this.enhanceCart(), 300);
    });

    // Simple observer for cart changes
    const observer = new MutationObserver(() => {
      if (document.querySelector('.cart-drawer:not([hidden])')) {
        setTimeout(() => this.enhanceCart(), 100);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden']
    });

    console.log('🎨 Plan Builder Cart Enhancer V2 initialized');
  }

  injectCSS() {
    if (this.cssInjected) return;
    
    const css = `
      /* Plan Builder Cart Styling - Injected CSS */
      .pb-storage { padding: 2px 0 !important; font-weight: 500 !important; margin-top: 12px !important; }
      .pb-color { padding: 2px 0 !important; font-weight: 500 !important; border-bottom: 1px solid rgba(0,0,0,0.1) !important; margin-bottom: 8px !important; padding-bottom: 12px !important; }
      .pb-pricing-option { padding: 2px 0 !important; font-weight: 400 !important; }
      .pb-device-price { padding: 2px 0 !important; font-weight: 400 !important; }
      .pb-device-monthly { padding: 2px 0 !important; font-weight: 400 !important; border-bottom: 1px solid rgba(0,0,0,0.1) !important; margin-bottom: 8px !important; padding-bottom: 12px !important; }
      .pb-rate-plan { padding: 2px 0 !important; font-weight: 400 !important; }
      .pb-coverage { padding: 2px 0 !important; font-weight: 400 !important; }
      .pb-activation-fee { padding: 2px 0 !important; font-weight: 400 !important; margin-bottom: 8px !important; }
      .pb-total-savings { 
        background-color: #00AA90 !important; 
        color: white !important; 
        font-weight: 700 !important; 
        padding: 10px 16px !important; 
        border-radius: 6px !important; 
        margin: 8px 0 !important; 
        border: 2px solid #006B5A !important; 
        display: block !important; 
        width: fit-content !important;
        box-shadow: 0 4px 8px rgba(0, 170, 144, 0.3) !important;
      }
    `;
    
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
    this.cssInjected = true;
    console.log('💉 CSS injected for Plan Builder cart styling');
  }

  enhanceCart() {
    try {
      // Only process if cart is visible
      const cartDrawer = document.querySelector('.cart-drawer');
      if (!cartDrawer || cartDrawer.hasAttribute('hidden')) {
        return;
      }

      const propertyElements = document.querySelectorAll('.horizontal-product__details .flex.gap-1.text-xs.text-opacity.leading-tight');
      
      propertyElements.forEach(element => {
        const dtElement = element.querySelector('dt');
        const ddElement = element.querySelector('dd');
        
        // Handle sr-only Storage and Color elements
        if (dtElement && dtElement.classList.contains('sr-only')) {
          const propertyName = dtElement.textContent.trim().replace(':', '');
          
          if (propertyName === 'Storage' && !ddElement.textContent.includes('Storage:')) {
            ddElement.textContent = `Storage: ${ddElement.textContent.trim()}`;
            element.className = element.className.replace(/pb-\w+/g, '') + ' pb-storage';
            console.log('✅ Added Storage label and class');
          } else if (propertyName === 'Color' && !ddElement.textContent.includes('Color:')) {
            ddElement.textContent = `Color: ${ddElement.textContent.trim()}`;
            element.className = element.className.replace(/pb-\w+/g, '') + ' pb-color';
            console.log('✅ Added Color label and class');
          }
          return;
        }
        
        // Handle regular properties
        if (dtElement && ddElement) {
          const propertyName = dtElement.textContent.trim().replace(':', '');
          console.log(`🔍 Processing property: "${propertyName}"`);
          
          // Debug activation fee specifically
          if (propertyName.toLowerCase().includes('activation')) {
            console.log(`🔍 Found activation-related property: "${propertyName}"`);
            console.log(`🔍 dtElement text: "${dtElement.textContent}"`);
            console.log(`🔍 ddElement text: "${ddElement.textContent}"`);
            console.log(`🔍 Full element:`, element);
          }
          
          // Remove any existing pb- classes and add new one
          element.className = element.className.replace(/pb-[\w-]+/g, '');
          
          switch (propertyName) {
            case 'Pricing Option':
              element.className += ' pb-pricing-option';
              break;
            case 'Device Price':
              element.className += ' pb-device-price';
              break;
            case 'Device Monthly':
              element.className += ' pb-device-monthly';
              break;
            case 'Rate Plan':
              element.className += ' pb-rate-plan';
              break;
            case 'Coverage':
              element.className += ' pb-coverage';
              break;
            case 'Activation Fee Compare At':
              // Hide this property - it's only used internally for discount calculation
              element.style.display = 'none';
              console.log('🙈 Hidden Activation Fee Compare At property from display');
              element.className += ' pb-activation-fee-compare-at-hidden';
              break;
            case 'Activation Fee':
            case 'Activation':
            case 'Fee':
              // Check for activation fee compare-at price from Plan Builder data
              const activationPrice = ddElement.textContent.trim();
              
              // Find the cart line item to get Plan Builder properties
              const cartLineItem = element.closest('.cart__item') || 
                                 element.closest('[data-line-item]') ||
                                 element.closest('.horizontal-product');
              
              let activationCompareAt = null;
              
              if (cartLineItem) {
                // Look for Plan Builder activation fee compare-at price in line item properties
                const propertyElements = cartLineItem.querySelectorAll('.horizontal-product__details .flex.gap-1.text-xs.text-opacity.leading-tight');
                
                propertyElements.forEach((propElement, index) => {
                  const dt = propElement.querySelector('dt');
                  const dd = propElement.querySelector('dd');
                  
                  if (dt && dd) {
                    const propName = dt.textContent.trim().replace(':', '');
                    const propValue = dd.textContent.trim();
                    
                    // Look for activation fee compare-at price property
                    if (propName === 'Activation Fee Compare At' || propName === '_activation_fee_compare_at') {
                      activationCompareAt = propValue;
                      console.log(`🎯 Found activation fee compare-at price: ${activationCompareAt}`);
                    }
                  }
                });
                
                // If no compare-at found in properties, check for standard Shopify compare-at price
                if (!activationCompareAt) {
                  activationCompareAt = cartLineItem.getAttribute('data-compare-at-price') ||
                                      cartLineItem.querySelector('[data-compare-at-price]')?.getAttribute('data-compare-at-price') ||
                                      cartLineItem.querySelector('.compare-at-price')?.textContent ||
                                      cartLineItem.querySelector('.was-price')?.textContent;
                }
              }
              
              console.log(`🔍 Activation Fee - Price: ${activationPrice}, Compare At: ${activationCompareAt}`);
              console.log(`🔍 Cart line item:`, cartLineItem);
              console.log(`🔍 Cart line item attributes:`, cartLineItem ? Array.from(cartLineItem.attributes).map(attr => `${attr.name}="${attr.value}"`).join(', ') : 'none');
              
              // Check if this is actually a separate activation fee product
              const handle = cartLineItem?.getAttribute('data-handle');
              const variantId = cartLineItem?.getAttribute('data-variant-id');
              console.log(`🔍 Product handle: ${handle}, Variant ID: ${variantId}`);
              
              // Look for activation fee as separate cart item
              const allCartItems = document.querySelectorAll('.cart__item, .horizontal-product, [data-line-item]');
              console.log(`🔍 Found ${allCartItems.length} cart items total`);
              
              allCartItems.forEach((item, index) => {
                const itemHandle = item.getAttribute('data-handle');
                const itemPrice = item.getAttribute('data-price');
                const itemVariantId = item.getAttribute('data-variant-id');
                console.log(`🔍 Cart item ${index}: handle="${itemHandle}", price="${itemPrice}", variant="${itemVariantId}"`);
                
                // Check if this looks like an activation fee product
                if (itemHandle && (itemHandle.includes('activation') || itemHandle.includes('fee'))) {
                  console.log(`🎯 Found potential activation fee product: ${itemHandle}`);
                }
              });
              
              // If there's a compare-at price and it's higher, show discount
              if (activationCompareAt && activationCompareAt !== activationPrice) {
                // Check if discount styling has already been applied
                if (ddElement.innerHTML.includes('line-through') || ddElement.innerHTML.includes('-67%') || ddElement.innerHTML.includes('background-color: #dc2626')) {
                  console.log('🔄 Discount styling already applied, skipping...');
                  break;
                }
                
                const price = parseFloat(activationPrice.replace(/[^0-9.]/g, ''));
                const compareAt = parseFloat(activationCompareAt.replace(/[^0-9.]/g, ''));
                
                if (compareAt > price && price >= 0) {
                  const savings = compareAt - price;
                  const savingsPercent = Math.round((savings / compareAt) * 100);
                  
                  ddElement.innerHTML = `${activationPrice} <span style="text-decoration: line-through; color: #9ca3af; font-size: 0.875em; margin-left: 6px;">${activationCompareAt}</span> <span style="background-color: #dc2626; color: white; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-size: 0.75em; margin-left: 6px;">-${savingsPercent}%</span>`;
                  console.log(`✅ Applied discount styling to Activation Fee: ${activationPrice} vs ${activationCompareAt} (${savingsPercent}% off)`);
                }
              }
              
              element.className += ' pb-activation-fee';
              break;
            case 'Device Savings':
              // Combine text into single line with green dollar value
              const savingsValue = ddElement.textContent.replace('Save ', '');
              element.innerHTML = `Total Savings: <span style="color: #00AA90 !important; font-weight: 700 !important;">${savingsValue}</span>`;
              element.className += ' pb-total-savings';
              
              // Force inline styles with !important - gray theme
              element.style.setProperty('background-color', 'rgba(107, 114, 128, 0.1)', 'important');
              element.style.setProperty('color', '#6b7280', 'important');
              element.style.setProperty('border', '1px solid #6b7280', 'important');
              element.style.setProperty('border-radius', '6px', 'important');
              element.style.setProperty('padding', '10px 16px', 'important');
              element.style.setProperty('display', 'block', 'important');
              element.style.setProperty('width', 'fit-content', 'important');
              element.style.setProperty('font-weight', '400', 'important');
              element.style.setProperty('white-space', 'nowrap', 'important');
              element.style.setProperty('text-align', 'left', 'important');
              element.style.setProperty('box-shadow', 'none', 'important');
              
              // Debug styling
              console.log('🔍 Total Savings element after styling:', element);
              console.log('🔍 Element classes:', element.className);
              console.log('🔍 Element innerHTML:', element.innerHTML);
              console.log('🔍 Element style object:', element.style);
              console.log('🔍 Computed border:', window.getComputedStyle(element).border);
              console.log('🔍 Computed background:', window.getComputedStyle(element).backgroundColor);
              console.log('🔍 Computed display:', window.getComputedStyle(element).display);
              console.log('🔍 Element visible?', element.offsetWidth > 0 && element.offsetHeight > 0);
              
              // Add separator after Total Savings
              this.addSeparatorAfter(element);
              console.log('✅ Updated Total Savings with inline styles and added separator');
              break;
          }
        }
      });

      console.log(`🎨 Enhanced ${propertyElements.length} cart properties with CSS classes`);
    } catch (error) {
      console.log('⚠️ Cart enhancement error:', error);
    }
  }

  addSeparatorAfter(element) {
    // Check if separator already exists
    if (element.nextElementSibling && element.nextElementSibling.classList.contains('pb-separator')) {
      return;
    }

    // Create separator element
    const separator = document.createElement('div');
    separator.className = 'pb-separator';
    separator.style.cssText = 'width: 100% !important; height: 1px !important; background-color: rgba(0,0,0,0.1) !important; margin: 12px 0 8px 0 !important; display: block !important; clear: both !important;';
    
    // Since nextSibling is null, we need to append to parent
    element.parentNode.appendChild(separator);
    console.log('📏 Added separator after Total Savings (appended to parent)');
    console.log('🔍 Separator element:', separator);
    console.log('🔍 Separator computed style:', window.getComputedStyle(separator));
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.planBuilderCartEnhancer = new PlanBuilderCartEnhancer();
  });
} else {
  window.planBuilderCartEnhancer = new PlanBuilderCartEnhancer();
}

// Export for manual initialization
window.PlanBuilderCartEnhancer = PlanBuilderCartEnhancer;
